import type { IAbstractFeature } from '../constants'

export interface Feature extends IAbstractFeature {
  type: 'feature'
  detect: string
}

export const FEATURES: Feature[] = [
  {
    id: 'feature-database',
    type: 'feature',
    title: 'Database',
    image: 'https://heyboss.heeyo.ai/design-system/Database_4TZnCIGT.png',
    prompt: 'Please add the database to my project.',
    detect: 'database',
  },
  {
    id: 'feature-login',
    type: 'feature',
    title: 'Login',
    image: 'https://heyboss.heeyo.ai/design-system/Login_bu07taG4.png',
    prompt: 'Add login feature',
    detect:
      'login form, login page, method call like supabase.auth.signInWithOtp(), supabase.auth.signInWithGoogle(), etc',
  },
  {
    id: 'feature-ai-chatbot',
    type: 'feature',
    title: 'AI Chatbot',
    image: 'https://heyboss.heeyo.ai/design-system/AI%20Chatbot_N54Rxbb4.png',
    prompt:
      "Add a collapsible AI chatbot that connects to OpenAI's LLM that greets visitors and asks about their day in a friendly way",
    detect: 'AI chatbot that connects to OpenAI LLM',
  },
  {
    id: 'feature-ai-photo-generator',
    type: 'feature',
    title: 'AI Photo Generator',
    image:
      'https://heyboss.heeyo.ai/design-system/AI%20Photo%20Generator_iWzVZzEX.png',
    prompt:
      'Add photo generator with text input box on the left and will generate an AI image on the right.',
    detect: 'an text to image generator app powered by AI',
  },
  {
    id: 'feature-voice-recorder',
    type: 'feature',
    title: 'Voice Recorder',
    image: 'https://heyboss.heeyo.ai/templates/voice_recorder.png',
    prompt: `Add a voice recorder that's compatible for mobile and desktop, and after hitting "record" start recording; after hitting" stop", stop recording and show the recorded audio with ability to replay or download`,
    detect: 'a voice recorder app',
  },
  {
    id: 'feature-ai-video-generator',
    type: 'feature',
    title: 'AI Video Generator',
    image: 'https://heyboss.heeyo.ai/templates/ai_video_generator.png',
    prompt:
      'Create an AI video generator where users can input text prompt on the left and see a generated AI video on the right with progress bar indicating progress',
    detect: 'a text to video generator app powered by AI',
  },

  {
    id: 'feature-social-sharing',
    type: 'feature',
    title: 'Social Media Sharing',
    image:
      'https://heyboss.heeyo.ai/design-system/20250311-203126_bOcPIRtz.jpeg',
    prompt:
      'Social Media Sharing: Add a social media sharing plugin that supports major social platforms (such as whatsapp, linkedin, Social, x, etc.), making it convenient for users to share content and increase website exposure.',
    detect: 'social media sharing buttons or components',
  },
  {
    id: 'feature-customer-reviews',
    type: 'feature',
    title: 'Customer Reviews & Ratings',
    image:
      'https://heyboss.heeyo.ai/design-system/Customer%20reviews_gquUgJZu.png',
    prompt:
      'Add a customer review plugin that allows users to submit ratings and comments, display the average rating and latest reviews, and enhance the credibility of the product or service. Enable the database and create a customer review storage table to store user-submitted review information.',
    detect: 'customer review and rating system components',
  },
  {
    id: 'feature-modal-dialog',
    type: 'feature',
    title: 'Modal Box & Dialog',
    image: 'https://heyboss.heeyo.ai/design-system/Modal%20box_8YyBAe3L.png',
    prompt:
      'Design a popup/modal plugin to display promotional information or important announcements. The popup should include promotional images, detailed description text, and a "Learn More" link, supporting automatic popup and manual closure by the user. Image link: xxxx; Details link: xxxx.',
    detect: 'modal or dialog component with promotional content',
  },
  {
    id: 'feature-site-search',
    type: 'feature',
    title: 'Site-wide Search',
    image:
      'https://heyboss.heeyo.ai/design-system/Site-wide%20search_0ULe-z6O.png',
    prompt:
      'Create a site-wide search plugin, including a search input box, search button, and search results display area. It should support auto-completion and search history, with each item in the result list accompanied by a jump link. Ensure all links and resource addresses are valid and the interface style is consistent with the overall website.',
    detect: 'search component with autocomplete functionality',
  },
  {
    id: 'feature-email-subscription',
    type: 'feature',
    title: 'Email Subscription',
    image:
      'https://heyboss.heeyo.ai/design-system/Email%20subscription_kCs91ZBY.png',
    prompt:
      "[Email Subscription] Add an email subscription form module that requires users to enter their email address and check a privacy policy confirmation box. After submitting the form, display a successful subscription message and record the user's subscription information for future email marketing. Enable the database and create an email storage table to store the submitted email information.",
    detect: 'email subscription form component',
  },
  {
    id: 'feature-contact-form',
    type: 'feature',
    title: 'Contact Us',
    image: 'https://heyboss.heeyo.ai/design-system/18_BPML5AzG.png',
    prompt:
      '[Contact Us] Design a "Contact Us" form module that includes fields for name, email, subject, and message content. Support multiple-choice feedback options. After submission, display a confirmation message and notify the administrator via email. Ensure a clean form design with strict data validation. Enable the database and create a "Contact Us" storage table to store user-submitted contact information. ',
    detect: 'contact form component with file upload',
  },

  {
    id: 'feature-dropdown-nav',
    type: 'feature',
    title: 'Dropdown Navigation',
    image:
      'https://heyboss.heeyo.ai/design-system/Dropdown%20navigation_Yw6Jj_hr.png',
    prompt:
      'Create a responsive navigation bar module, including a company logo (image), menu items (text), and dropdown submenus, with each menu item accompanied by a jump link. Ensure compatibility with both mobile and desktop devices, and that all links and icon addresses are correctly configured. Logo image link: xxxx; Menu item links: xxxx.',
    detect: 'responsive navigation bar with dropdown menus',
  },

  {
    id: 'feature-snow-effect',
    type: 'feature',
    title: 'Christmas Snowflake',
    image:
      'https://heyboss.heeyo.ai/design-system/Christmas%20snowflake%20effect_Oi5jNNtI.png',
    prompt:
      'Display Christmas snowflake and gift effects in full screen on the interface.The size of the snowflake effects is randomly displayed (snowflake styles need to be generated), and the lifespan of each snowflake effect is random.The size of the Christmas gift effects is randomly displayed, and the lifespan of each Christmas gift effect is random.Simulate the effect of wind blowing (the wind sometimes blows strongly, sometimes lightly).The number of snowflake effects increases continuously for a period of time and then decreases continuously (the number of snowflakes cycles between increasing and decreasing).',
    detect: 'Christmas themed animation effects with snowflakes',
  },

  {
    id: 'feature-cursor-trail',
    type: 'feature',
    title: 'Cursor Firefly Trail',
    image:
      'https://heyboss.heeyo.ai/design-system/Cursor%20firefly%20trail%20effect_LSVbB1tv.png',
    prompt: `On a webpage, when the mouse cursor moves, it creates a trailing effect similar to fireflies flying. The trail is composed of a series of light points or particles that are generated as the cursor moves and gradually dissipate.

Light Point Styles:
The shape of the light points can be circular, square, or other custom shapes.
The color of the light points can be a single color, multiple colors randomly distributed, or change according to specific rules.
The size of the light points can be fixed or vary according to the lifecycle of the light points (e.g., gradually becoming smaller).
The light points can have a glowing effect or added visual effects such as shadows or blur.

Trail Shape:
The shape of the trail can be linear, curved, spiral, etc.
The length of the trail can be adjusted, i.e., the duration the light points stay on the screen.

Animation Effects:
The appearance and dissipation of the light points can have different animation effects, such as fade in/out, scaling, moving, etc.
The light points can have random motion trajectories to increase the sense of naturalness.

Interaction Effects:
Responsiveness: When the cursor moves, the trailing effect should respond in real-time with no noticeable delay.
Sensitivity: The trailing effect should accurately follow the cursor movement, avoiding misalignment or jitter.`,
    detect: 'cursor trail animation effect',
  },
  {
    id: 'feature-footer',
    type: 'feature',
    title: 'Website Footer',
    image:
      'https://heyboss.heeyo.ai/design-system/Website%20footer_D3qe5F0-.png',
    prompt: `Add a website footer component that displays contact information, navigation links, social media, and a subscription form, ensuring the information is complete.

The footer adopts a column layout, with background support for light and dark modes. The text and icons are clear, and it supports custom borders and spacing. The design style is simple and professional.

Interaction performance: The footer fades in when loading, links and buttons change color when hovered, and the subscription form supports instant validation and feedback.`,
    detect: 'website footer component with multiple sections',
  },
  {
    id: 'feature-back-to-top-button',
    type: 'feature',
    title: 'Back to Top Button',
    image:
      'https://heyboss.heeyo.ai/design-system/Back%20to%20Top%20Button_i8jxSEux.png',
    prompt:
      'A "Back to Top" button component that is fixed at the bottom right corner of the page. When the user clicks the button, the page smoothly scrolls back to the top. The component should support automatic show/hide functionality (e.g., appear when the page is scrolled down a certain distance and hide when back at the top). It should be compatible with different page layouts and content lengths. This button enhances user navigation, especially for long or content-rich pages.',
    detect: 'back to top button',
  },
  {
    id: 'feature-right-click-protection',
    type: 'feature',
    title: 'Right-Click Protection Program',
    image:
      'https://heyboss.heeyo.ai/design-system/Right-Click%20Protection%20Program_BqLNiorD.png',
    prompt:
      'Content Protection Program: This component is designed to prevent unauthorized copying of website content. When visitors attempt to right-click or use shortcut keys (such as Ctrl+C or Ctrl+V) to copy text or media, a copyright protection message is displayed. The component allows customization of the message, button styles, and close button options.',
    detect: 'right-click protection program',
  },
  {
    id: 'feature-countdown-bar',
    type: 'feature',
    title: 'Countdown Bar',
    image:
      'https://heyboss.heeyo.ai/design-system/Countdown%20Bar_LD9k4xlV.png',
    prompt: `A banner-style countdown displayed at the top or bottom of the page, clearly informing users of the remaining time for a promotion or special offer (showing <DAYS> days, <HOURS> hours, <MINUTES> minutes, <SECONDS> seconds).

Event Text and Description:
The countdown area displays event text, such as <SPECIAL_OFFER_TEXT>, along with an explanatory message before the countdown (e.g., <SALE_ENDS_IN_TEXT>).

CTA Button:
Includes a prominent call-to-action button with the text <CTA_BUTTON_TEXT>. Clicking the button redirects users to the event details page or promotion link.

Real-time Updates:
The countdown updates in real time. When the timer reaches zero, the component automatically switches to a message like <ACTIVITY_ENDED_TEXT> or hides the countdown bar.`,
    detect: 'countdown bar',
  },
  {
    id: 'feature-cookies-consent-bar',
    type: 'feature',
    title: 'Cookies Consent Bar',
    image:
      'https://heyboss.heeyo.ai/design-system/Cookies%20Consent%20Bar_62cv0IMp.png',
    prompt: `User Notification:
Displays a banner at the bottom of the page upon loading, informing users that the website uses cookies to enhance their experience and providing information about the privacy policy.
User Consent Options:
Provides clear options such as "Accept" and "Decline" or "Learn More" buttons.

When users click "Accept," the banner automatically disappears, and the consent status is stored in the browser to prevent repeated prompts.
If users click "Decline" or "Learn More," they can be redirected to a detailed privacy policy page or an additional information popup.`,
    detect: 'cookies consent bar',
  },
]
